/**
 * AI visibility (REQ-016 AC-005).
 *
 * Is HealthPass in the answer when a traveller asks an assistant?
 *
 * The page is built around one constraint: **a single figure is never shown**.
 * Brand visibility in generated answers is unstable between identical runs, so
 * one number is noise wearing a percentage sign. Every rate here arrives with
 * the sample it came from and the spread that was observed, and below the
 * minimum sample it says "not enough samples" instead of a figure — because a
 * figure with a caveat beside it gets copied into a slide without the caveat.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Loader2,
  Play,
  Quote,
} from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

interface Rate {
  value: number | null;
  n: number;
  spread: { low: number; high: number } | null;
  insufficient: boolean;
}

interface Run {
  runId: string;
  at: string;
  assistants: string[];
  mentionRate: Rate;
  citationRate: Rate;
  shareOfVoice: Rate;
  failures: number;
  competitorsNamed: { name: string; count: number }[];
}

interface Readiness {
  meetsRequirement: boolean;
  reason: string;
  samplesPerPrompt: number;
  prompts: number;
  configured: { id: string; label: string; vendor: string; model: string }[];
  missing: { id: string; label: string; vendor: string; needs: string }[];
}

const percent = (value: number) => `${Math.round(value * 100)}%`;

/**
 * A rate, or an honest refusal to give one.
 *
 * The spread is not decoration: it is the number that shows the same question
 * answered differently, which is the whole reason this is a series.
 */
function RateCell({ rate, label }: { rate: Rate; label: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      {rate.insufficient || rate.value === null ? (
        <>
          <p className="mt-1 text-lg font-medium text-gray-400">
            Not enough samples
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {rate.n} so far. A rate from fewer than three answers is noise.
          </p>
        </>
      ) : (
        <>
          <p className="mt-1 text-3xl font-semibold tabular-nums text-gray-900">
            {percent(rate.value)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            n = {rate.n}
            {rate.spread && (
              <>
                {" · "}
                per-question spread {percent(rate.spread.low)}–
                {percent(rate.spread.high)}
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}

export default function AioPage() {
  const fmt = useLocaleFormat();
  const queryClient = useQueryClient();
  const [note, setNote] = useState<string | null>(null);

  const readiness = useQuery({
    queryKey: ["aio", "readiness"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Readiness }>(
        "/staff/marketing/aio/readiness",
      );
      return response.data.data;
    },
  });

  const series = useQuery({
    queryKey: ["aio", "series"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: { runs: Run[] } }>(
        "/staff/marketing/aio/series",
      );
      return response.data.data.runs;
    },
  });

  const sweep = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{
        data: { asked: number; failed: number; skipped?: string };
      }>("/staff/marketing/aio/run", {});
      return response.data.data;
    },
    onSuccess: (result) => {
      setNote(
        result.skipped
          ? result.skipped
          : `Asked ${result.asked} questions, ${result.failed} failed.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["aio"] });
    },
    onError: () => setNote("The sweep could not be started."),
  });

  const latest = series.data?.[0];

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Bot className="h-5 w-5 text-sky-600" /> AI visibility
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Whether an assistant names HealthPass when a traveller asks it one
            of {readiness.data?.prompts ?? "our"} questions.
          </p>
        </div>
        <button
          onClick={() => sweep.mutate()}
          disabled={
            sweep.isPending || (readiness.data?.configured.length ?? 0) === 0
          }
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          {sweep.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          Run now
        </button>
      </div>

      {/* Whether anything can be measured at all. Deliberately above the
          numbers: an empty series and "we are never mentioned" look identical
          on a chart, and only one of them is about our marketing. */}
      {readiness.data && (
        <div
          className={`flex items-start gap-3 rounded-lg border p-4 text-sm ${
            readiness.data.meetsRequirement
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
        >
          {readiness.data.meetsRequirement ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div>
            <p className="font-medium">{readiness.data.reason}</p>
            {readiness.data.missing.length > 0 && (
              <p className="mt-1">
                Not configured:{" "}
                {readiness.data.missing
                  .map((a) => `${a.label} (needs ${a.needs})`)
                  .join(", ")}
                .
              </p>
            )}
            <p className="mt-1">
              Each question is asked {readiness.data.samplesPerPrompt} times per
              assistant, weekly. Answers differ between identical runs, so a
              single check proves nothing.
            </p>
          </div>
        </div>
      )}

      {note && <p className="text-sm text-gray-600">{note}</p>}

      {series.isLoading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the series…
        </div>
      ) : !latest ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-600">
          Nothing has been sampled yet, so there is no series. This is not a
          reading of zero.
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <RateCell rate={latest.mentionRate} label="Mentioned" />
            <RateCell rate={latest.citationRate} label="Cited (linked)" />
            <RateCell rate={latest.shareOfVoice} label="Share of voice" />
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Run</th>
                  <th className="px-4 py-2 font-medium">Mentioned</th>
                  <th className="px-4 py-2 font-medium">Cited</th>
                  <th className="px-4 py-2 font-medium">Share of voice</th>
                  <th className="px-4 py-2 font-medium">Samples</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {series.data?.map((run) => (
                  <tr key={run.runId}>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {fmt.date(run.at)}
                    </td>
                    {[run.mentionRate, run.citationRate, run.shareOfVoice].map(
                      (rate, index) => (
                        <td
                          key={index}
                          className="px-4 py-3 tabular-nums text-gray-900"
                        >
                          {rate.insufficient || rate.value === null ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <>
                              {percent(rate.value)}
                              {rate.spread && (
                                <span className="ml-1 text-xs text-gray-500">
                                  ({percent(rate.spread.low)}–
                                  {percent(rate.spread.high)})
                                </span>
                              )}
                            </>
                          )}
                        </td>
                      ),
                    )}
                    <td className="px-4 py-3 tabular-nums text-gray-600">
                      {run.mentionRate.n}
                      {run.failures > 0 && (
                        <span className="ml-1 text-xs text-amber-700">
                          +{run.failures} failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {latest.competitorsNamed.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Quote className="h-3.5 w-3.5" /> Named instead, in the latest
                run
              </p>
              <ul className="mt-2 flex flex-wrap gap-2 text-sm">
                {latest.competitorsNamed.map((entry) => (
                  <li
                    key={entry.name}
                    className="rounded-full bg-gray-100 px-3 py-1 text-gray-700"
                  >
                    {entry.name}{" "}
                    <span className="tabular-nums text-gray-500">
                      ×{entry.count}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
